// Three.js view of the cube: instanced rounded cubies + instanced stickers.
// The view is a pure function of (model state, animation pose): cubie slots
// are static; sticker colors are re-read from the model on commit. A layer
// turn is shown as a rigid rotation of the affected slots.

import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { cellToPos } from "../cube/model.js";
import { FACE_COLORS, qualityPreset, renderPixelRatio } from "./quality.js";

const FACE_ROTATIONS = {
  // face -> euler rotation applied to the sticker geometry (quad in XY plane)
  0: new THREE.Euler(-Math.PI / 2, 0, 0), // U: +y
  1: new THREE.Euler(Math.PI / 2, 0, 0), // D: -y
  2: new THREE.Euler(0, Math.PI / 2, 0), // R: +x
  3: new THREE.Euler(0, -Math.PI / 2, 0), // L: -x
  4: new THREE.Euler(0, 0, 0), // F: +z
  5: new THREE.Euler(0, Math.PI, 0), // B: -z
};

const NORMALS = [
  [0, 1, 0],
  [0, -1, 0],
  [1, 0, 0],
  [-1, 0, 0],
  [0, 0, 1],
  [0, 0, -1],
];

function buildSurfaceTexture({ metal = false, repeat = 1 } = {}) {
  const size = 128;
  const data = new Uint8Array(size * size * 4);
  const noise = new Float32Array(size * size);
  let seed = metal ? 0x51f15e : 0x7e11a9;
  const random = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 0x100000000;
  };

  for (let i = 0; i < noise.length; i++) noise[i] = random() - 0.5;

  const radius = metal ? 2 : 1;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let local = 0;
      let samples = 0;
      for (let oy = -radius; oy <= radius; oy++) {
        for (let ox = -radius; ox <= radius; ox++) {
          const sx = (x + ox + size) % size;
          const sy = (y + oy + size) % size;
          local += noise[sy * size + sx];
          samples++;
        }
      }

      // Isotropic micrograin avoids the parallel ridges that read as FDM layers.
      const grain = metal
        ? 174 + (local / samples) * 52 + (random() - 0.5) * 5
        : 150 + (local / samples) * 38 + (random() - 0.5) * 8;
      const value = Math.max(0, Math.min(255, Math.round(grain)));
      const index = (y * size + x) * 4;
      data[index] = value;
      data[index + 1] = value;
      data[index + 2] = value;
      data[index + 3] = 255;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export class CubeView {
  constructor(container, { deviceClass = "high" } = {}) {
    this.container = container;
    this.deviceClass = deviceClass;
    this.N = 3;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(renderPixelRatio(window.devicePixelRatio));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.autoClear = false;
    container.appendChild(this.renderer.domElement);

    this.metalSurfaceTexture = buildSurfaceTexture({ metal: true, repeat: 3 });
    this.stickerSurfaceTexture = buildSurfaceTexture({ repeat: 2.5 });
    const anisotropy = Math.min(
      4,
      this.renderer.capabilities.getMaxAnisotropy(),
    );
    this.metalSurfaceTexture.anisotropy = anisotropy;
    this.stickerSurfaceTexture.anisotropy = anisotropy;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      36,
      container.clientWidth / container.clientHeight,
      0.1,
      500,
    );
    this.camera.position.set(5, 4.5, 6.5);
    this.rearCamera = new THREE.PerspectiveCamera(36, 1, 0.1, 500);
    this.rearView = { x: 12, y: 12, width: 200, height: 144 };
    this._layoutRearView(container.clientWidth, container.clientHeight);

    // Lighting: soft studio setup with environment reflections.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(
      new RoomEnvironment(),
      0.04,
    ).texture;
    pmrem.dispose();
    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(6, 9, 5);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xbdd0ff, 0.7);
    rim.position.set(-7, -3, -6);
    this.scene.add(rim);
    this.scene.add(new THREE.HemisphereLight(0xf4f6fa, 0x30323a, 0.5));

    // Fake contact shadow on a soft radial gradient.
    this.shadowPlane = this._buildShadowPlane();
    this.scene.add(this.shadowPlane);

    this.cubeGroup = new THREE.Group();
    this.scene.add(this.cubeGroup);

    this.cubieMesh = null;
    this.stickerMeshes = [];
    this.quality = null;
    this.cubiePositions = []; // instanceId -> {x,y,z,index}
    this.positionIndex = new Map(); // "x,y,z" -> cubie instance id
    this.layerCubies = new Map(); // "axis:layer" -> cubie instance ids
    this.layerStickers = new Map(); // "axis:layer" -> sticker (face,cell,instanceId) list
    this._matrix = new THREE.Matrix4();
    this._quat = new THREE.Quaternion();
    this._axisVec = new THREE.Vector3();
    this._tmp = new THREE.Matrix4();
    this._color = new THREE.Color();

    this.buildCube(3);
  }

  _buildShadowPlane() {
    const size = 256;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    const g = ctx.createRadialGradient(
      size / 2,
      size / 2,
      size * 0.08,
      size / 2,
      size / 2,
      size / 2,
    );
    g.addColorStop(0, "rgba(0,0,0,0.55)");
    g.addColorStop(0.55, "rgba(0,0,0,0.18)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
    });
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    plane.rotation.x = -Math.PI / 2;
    plane.renderOrder = -1;
    return plane;
  }

  buildCube(N) {
    this.N = N;
    this.quality = qualityPreset(N, this.deviceClass);
    // Dispose old meshes
    this.cubeGroup.clear();
    if (this.cubieMesh) {
      this.cubieMesh.geometry.dispose();
      this.cubieMesh.material.dispose();
    }
    for (const m of this.stickerMeshes) {
      m.geometry.dispose();
      m.material.dispose();
    }
    this.stickerMeshes = [];
    this.positionIndex.clear();
    this.layerCubies.clear();
    this.layerStickers.clear();

    const q = this.quality;
    const half = (N - 1) / 2;

    // Visible cubies only. Enumerating the six shells directly keeps cube
    // construction O(N²); scanning the full volume made large cubes needlessly
    // expensive even though internal cubies are never rendered.
    const positions = [];
    for (let x = 0; x < N; x++) {
      for (let y = 0; y < N; y++) {
        positions.push([x, y, 0]);
        if (N > 1) positions.push([x, y, N - 1]);
      }
    }
    for (let x = 0; x < N; x++) {
      for (let z = 1; z < N - 1; z++) {
        positions.push([x, 0, z]);
        positions.push([x, N - 1, z]);
      }
    }
    for (let y = 1; y < N - 1; y++) {
      for (let z = 1; z < N - 1; z++) {
        positions.push([0, y, z]);
        positions.push([N - 1, y, z]);
      }
    }

    const cubieGeo = new RoundedBoxGeometry(
      q.cubieScale,
      q.cubieScale,
      q.cubieScale,
      q.cubieSegments,
      q.cubieRadius,
    );
    const cubieMat = new THREE.MeshPhysicalMaterial({
      color: 0x575279,
      roughness: 0.58,
      roughnessMap: this.metalSurfaceTexture,
      bumpMap: this.metalSurfaceTexture,
      bumpScale: 0.0035,
      metalness: 0.86,
      clearcoat: 0.28,
      clearcoatRoughness: 0.3,
      envMapIntensity: 1.18,
    });
    this.cubieMesh = new THREE.InstancedMesh(
      cubieGeo,
      cubieMat,
      positions.length,
    );
    this.cubieMesh.count = positions.length;
    this.cubiePositions = positions.map(([x, y, z], i) => {
      this.positionIndex.set(`${x},${y},${z}`, i);
      this._matrix.makeTranslation(x - half, y - half, z - half);
      this.cubieMesh.setMatrixAt(i, this._matrix);
      return { x, y, z, index: i };
    });
    this.cubieMesh.instanceMatrix.needsUpdate = true;
    this.cubeGroup.add(this.cubieMesh);

    // Stickers: one InstancedMesh per face with per-instance colors.
    const stickerGeo = this._stickerGeometry(q);
    const stickerMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.64,
      roughnessMap: this.stickerSurfaceTexture,
      bumpMap: this.stickerSurfaceTexture,
      bumpScale: 0.003,
      metalness: 0.04,
      clearcoat: 0.52,
      clearcoatRoughness: 0.24,
      envMapIntensity: 1.08,
    });
    const eps = q.cubieScale / 2 - q.stickerInset;
    for (let f = 0; f < 6; f++) {
      const geo = stickerGeo.clone();
      geo.applyQuaternion(
        new THREE.Quaternion().setFromEuler(FACE_ROTATIONS[f]),
      );
      const mesh = new THREE.InstancedMesh(geo, stickerMat, N * N);
      mesh.count = N * N;
      const [nx, ny, nz] = NORMALS[f];
      for (let u = 0; u < N; u++) {
        for (let v = 0; v < N; v++) {
          const [x, y, z] = cellToPos(f, u, v, N);
          this._matrix.makeTranslation(
            x - half + nx * eps,
            y - half + ny * eps,
            z - half + nz * eps,
          );
          mesh.setMatrixAt(u * N + v, this._matrix);
        }
      }
      mesh.instanceMatrix.needsUpdate = true;
      this.stickerMeshes.push(mesh);
      this.cubeGroup.add(mesh);
    }
    stickerGeo.dispose();

    // Layer membership caches. Populate each visible item into its three
    // layers once, instead of rescanning the surface for every layer.
    for (let axis = 0; axis < 3; axis++) {
      for (let layer = 0; layer < N; layer++) {
        const key = `${axis}:${layer}`;
        this.layerCubies.set(key, []);
        this.layerStickers.set(key, []);
      }
    }
    for (let index = 0; index < positions.length; index++) {
      const position = positions[index];
      for (let axis = 0; axis < 3; axis++) {
        this.layerCubies.get(`${axis}:${position[axis]}`).push(index);
      }
    }
    for (let f = 0; f < 6; f++) {
      for (let cell = 0; cell < N * N; cell++) {
        const position = cellToPos(f, (cell / N) | 0, cell % N, N);
        for (let axis = 0; axis < 3; axis++) {
          this.layerStickers.get(`${axis}:${position[axis]}`).push({ f, cell });
        }
      }
    }

    // Shadow plane sized to the cube.
    const extent = N * 1.35;
    this.shadowPlane.scale.set(extent, extent, 1);
    this.shadowPlane.position.y = -half - Math.max(0.7, N * 0.08);
  }

  _stickerGeometry(q) {
    // Rounded-rect quad in the XY plane, centered at the origin.
    const s = q.stickerScale / 2;
    const r = q.stickerRadius;
    const shape = new THREE.Shape();
    const x = -s,
      y = -s,
      w = q.stickerScale,
      h = q.stickerScale;
    shape.moveTo(x + r, y);
    shape.lineTo(x + w - r, y);
    shape.quadraticCurveTo(x + w, y, x + w, y + r);
    shape.lineTo(x + w, y + h - r);
    shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    shape.lineTo(x + r, y + h);
    shape.quadraticCurveTo(x, y + h, x, y + h - r);
    shape.lineTo(x, y + r);
    shape.quadraticCurveTo(x, y, x + r, y);
    return new THREE.ExtrudeGeometry(shape, {
      depth: q.stickerDepth,
      steps: 1,
      curveSegments: q.stickerSegments * 2 + 1,
      bevelEnabled: true,
      bevelThickness: q.stickerBevel,
      bevelSize: q.stickerBevel,
      bevelSegments: q.stickerBevelSegments,
    });
  }

  // Write sticker colors from the model state.
  setState(state) {
    const area = this.N * this.N;
    for (let f = 0; f < 6; f++) {
      const mesh = this.stickerMeshes[f];
      for (let cell = 0; cell < area; cell++) {
        const colorIdx = (state[f][cell] / area) | 0;
        this._color.setHex(FACE_COLORS[colorIdx]);
        mesh.setColorAt(cell, this._color);
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  // Pose the moving layer(s) at `angle` radians for a move. angle sign follows
  // the move's direction; after commit call resetPose().
  setTurnPose(move, angle) {
    this._axisVec.set(0, 0, 0);
    this._axisVec.setComponent(move.axis, 1);
    this._quat.setFromAxisAngle(this._axisVec, angle);
    this._tmp.makeRotationFromQuaternion(this._quat);
    for (const layer of move.layers) {
      const key = `${move.axis}:${layer}`;
      const cubies = this.layerCubies.get(key);
      const half = (this.N - 1) / 2;
      for (const ci of cubies) {
        const p = this.cubiePositions[ci];
        this._matrix.makeTranslation(p.x - half, p.y - half, p.z - half);
        this._matrix.premultiply(this._tmp);
        this.cubieMesh.setMatrixAt(ci, this._matrix);
      }
      const stickers = this.layerStickers.get(key);
      const eps = this.quality.cubieScale / 2 - this.quality.stickerInset;
      for (const st of stickers) {
        const mesh = this.stickerMeshes[st.f];
        const [x, y, z] = cellToPos(
          st.f,
          (st.cell / this.N) | 0,
          st.cell % this.N,
          this.N,
        );
        const [nx, ny, nz] = NORMALS[st.f];
        this._matrix.makeTranslation(
          x - half + nx * eps,
          y - half + ny * eps,
          z - half + nz * eps,
        );
        this._matrix.premultiply(this._tmp);
        mesh.setMatrixAt(st.cell, this._matrix);
      }
    }
    this.cubieMesh.instanceMatrix.needsUpdate = true;
    for (const m of this.stickerMeshes) m.instanceMatrix.needsUpdate = true;
  }

  // Restore the rest pose (after a turn commits).
  resetPose(move) {
    const half = (this.N - 1) / 2;
    const eps = this.quality.cubieScale / 2 - this.quality.stickerInset;
    if (move) {
      const touchedFaces = new Set();
      for (const layer of move.layers) {
        const key = `${move.axis}:${layer}`;
        for (const ci of this.layerCubies.get(key)) {
          const p = this.cubiePositions[ci];
          this._matrix.makeTranslation(p.x - half, p.y - half, p.z - half);
          this.cubieMesh.setMatrixAt(ci, this._matrix);
        }
        for (const st of this.layerStickers.get(key)) {
          const mesh = this.stickerMeshes[st.f];
          const [x, y, z] = cellToPos(
            st.f,
            (st.cell / this.N) | 0,
            st.cell % this.N,
            this.N,
          );
          const [nx, ny, nz] = NORMALS[st.f];
          this._matrix.makeTranslation(
            x - half + nx * eps,
            y - half + ny * eps,
            z - half + nz * eps,
          );
          mesh.setMatrixAt(st.cell, this._matrix);
          touchedFaces.add(st.f);
        }
      }
      this.cubieMesh.instanceMatrix.needsUpdate = true;
      for (const face of touchedFaces)
        this.stickerMeshes[face].instanceMatrix.needsUpdate = true;
      return;
    }

    for (let i = 0; i < this.cubiePositions.length; i++) {
      const p = this.cubiePositions[i];
      this._matrix.makeTranslation(p.x - half, p.y - half, p.z - half);
      this.cubieMesh.setMatrixAt(i, this._matrix);
    }
    this.cubieMesh.instanceMatrix.needsUpdate = true;
    for (let f = 0; f < 6; f++) {
      const mesh = this.stickerMeshes[f];
      const [nx, ny, nz] = NORMALS[f];
      for (let cell = 0; cell < this.N * this.N; cell++) {
        const [x, y, z] = cellToPos(
          f,
          (cell / this.N) | 0,
          cell % this.N,
          this.N,
        );
        this._matrix.makeTranslation(
          x - half + nx * eps,
          y - half + ny * eps,
          z - half + nz * eps,
        );
        mesh.setMatrixAt(cell, this._matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  // Raycast against cubies; returns {x,y,z} of the hit cubie or null.
  pickCubie(ndc) {
    const raycaster =
      this._raycaster || (this._raycaster = new THREE.Raycaster());
    raycaster.setFromCamera(ndc, this.camera);
    const hit = raycaster.intersectObject(this.cubieMesh, false)[0];
    if (!hit) return null;
    const p = this.cubiePositions[hit.instanceId];
    return { x: p.x, y: p.y, z: p.z, instanceId: hit.instanceId };
  }

  // Project a world position to NDC.
  worldToNdc(x, y, z) {
    const v = this._v3 || (this._v3 = new THREE.Vector3());
    v.set(x, y, z);
    v.project(this.camera);
    return v;
  }

  resize() {
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    const nextPixelRatio = renderPixelRatio(window.devicePixelRatio);
    if (this.renderer.getPixelRatio() !== nextPixelRatio)
      this.renderer.setPixelRatio(nextPixelRatio);
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this._layoutRearView(w, h);
  }

  render() {
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    this.renderer.setScissorTest(false);
    this.renderer.setViewport(0, 0, w, h);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, true, true);
    this.renderer.render(this.scene, this.camera);

    this._syncRearCamera();
    const rear = this.rearView;
    this.renderer.setScissorTest(true);
    this.renderer.setScissor(rear.x, rear.y, rear.width, rear.height);
    this.renderer.setViewport(rear.x, rear.y, rear.width, rear.height);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, true, true);
    this.renderer.render(this.scene, this.rearCamera);
    this.renderer.setScissorTest(false);
    this.renderer.setViewport(0, 0, w, h);
    this.renderer.setClearColor(0x000000, 0);
  }

  _syncRearCamera() {
    this.rearCamera.position.copy(this.camera.position).multiplyScalar(-1);
    this.rearCamera.up.copy(this.camera.up);
    this.rearCamera.lookAt(0, 0, 0);
  }

  _layoutRearView(stageWidth, stageHeight) {
    const margin = stageWidth < 560 ? 8 : 12;
    const width = Math.round(Math.min(220, Math.max(132, stageWidth * 0.23)));
    const height = Math.round(Math.min(stageHeight - margin * 2, width * 0.72));
    this.rearView = { x: margin, y: margin, width, height };
    this.rearCamera.aspect = width / Math.max(1, height);
    this.rearCamera.updateProjectionMatrix();
    const styleHost = this.container.parentElement || this.container;
    styleHost.style.setProperty("--rear-view-width", `${width}px`);
    styleHost.style.setProperty("--rear-view-height", `${height}px`);
    styleHost.style.setProperty("--rear-view-margin", `${margin}px`);
  }

  dispose() {
    this.cubeGroup.clear();
    this.cubieMesh?.geometry.dispose();
    this.cubieMesh?.material.dispose();
    for (const mesh of this.stickerMeshes) mesh.geometry.dispose();
    this.metalSurfaceTexture.dispose();
    this.stickerSurfaceTexture.dispose();
    this.renderer.dispose();
    this.container.removeChild(this.renderer.domElement);
  }
}
